import { useId, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import type {
  AdminPlatformApp,
  PlatformAppComponent,
  PlatformAppDeployment,
  PlatformAppDetail,
  PlatformAppOperation,
  PlatformAppOperationRequest,
} from '@traceio-org/platform-contracts';
import { PageHeading, PageSection, RecordStatus, type RecordStatusTone } from '@traceio-org/ui';
import type { WriteFailure } from '../api';
import { AdminPage } from '../components/AdminPage';
import { ActionLink, AdminProblem } from '../components/AdminProblem';
import { LoadingBlock, SavedNotice, useNow } from '../components/feedback';
import { CheckboxGroup } from '../components/fields';
import { DateTimeText, ScrollTable } from '../components/records';
import { WriteDialog, useDialogTarget } from '../components/WriteDialog';
import { useAbilities, useAdmin } from '../context';
import { formatDateTime } from '../format';
import { APP_STATUS, ENVIRONMENT_NAMES, describeFailure, formatAge } from '../health';
import {
  APP_STATE,
  COMPONENT_KINDS,
  OPERATIONS,
  controlBlocked,
  deploymentWords,
  useDeploymentFollow,
} from '../platformApps';
import { forgetReads } from '../readMemory';
import { useAdminRead } from '../useAdminRead';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const OUTCOME_TONES: Readonly<Record<PlatformAppDeployment['outcome'], RecordStatusTone>> = {
  success: 'positive',
  failed: 'danger',
  canceled: 'neutral',
  in_progress: 'info',
};

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function External({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

/** Instances: a fixed count or the autoscaling range, with how many are ready now when DigitalOcean says. */
function instancesOf(component: PlatformAppComponent): string {
  const planned = component.autoscaling
    ? `${component.autoscaling.min} to ${component.autoscaling.max}, autoscaled`
    : component.instanceCount !== null
      ? String(component.instanceCount)
      : null;
  const health = component.health;
  const ready =
    health && health.replicasReady !== null && health.replicasDesired !== null
      ? health.replicasReady === health.replicasDesired
        ? `${health.replicasReady} ready`
        : `${health.replicasReady} of ${health.replicasDesired} ready`
      : null;
  if (planned && ready) return `${planned} (${ready})`;
  return planned ?? ready ?? 'n/a';
}

function ComponentState({ component }: { component: PlatformAppComponent }) {
  if (!component.health) {
    return (
      <span className="admin-muted">{component.kind === 'job' ? 'Runs on its trigger' : 'Not reported'}</span>
    );
  }
  const tone: RecordStatusTone =
    component.health.state === 'healthy'
      ? 'positive'
      : component.health.state === 'unhealthy'
        ? 'danger'
        : 'neutral';
  const label =
    component.health.state === 'healthy'
      ? 'Healthy'
      : component.health.state === 'unhealthy'
        ? 'Unhealthy'
        : 'Unknown';
  return <RecordStatus tone={tone}>{label}</RecordStatus>;
}

function ComponentsTable({
  app,
  restartBlocked,
  restartReasonId,
  onRestart,
}: {
  app: PlatformAppDetail;
  restartBlocked: string | null;
  /** The visible line that says why restart is off (the row buttons point to it). */
  restartReasonId: string | undefined;
  onRestart: (component: string) => void;
}) {
  return (
    <ScrollTable caption="Components" className="admin-table admin-table--app-components">
      <thead>
        <tr>
          <th scope="col">Component</th>
          <th scope="col">Kind</th>
          <th scope="col">Size</th>
          <th scope="col">Instances</th>
          <th scope="col">State</th>
          <th scope="col">
            <span className="admin-sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {app.components.map((component) => (
          <tr key={`${component.kind}:${component.name}`}>
            <th scope="row">
              {component.name}
              {component.jobKind ? <span className="admin-cell-secondary">{component.jobKind}</span> : null}
            </th>
            <td>{COMPONENT_KINDS[component.kind]}</td>
            <td>
              {component.instanceSize ? (
                <code>{component.instanceSize}</code>
              ) : (
                <span className="admin-muted">n/a</span>
              )}
            </td>
            <td>{instancesOf(component)}</td>
            <td>
              <ComponentState component={component} />
            </td>
            <td>
              <span className="admin-row-actions">
                {component.restartable ? (
                  <button
                    type="button"
                    className="admin-action"
                    aria-disabled={restartBlocked ? true : undefined}
                    aria-label={`Restart ${component.name}`}
                    aria-describedby={restartReasonId}
                    onClick={() => {
                      if (!restartBlocked) onRestart(component.name);
                    }}
                  >
                    Restart
                  </button>
                ) : null}
                <a
                  className="admin-action"
                  href={component.logsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Logs of ${component.name} in DigitalOcean`}
                >
                  Logs
                </a>
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

function DeploymentsTable({ deployments, now }: { deployments: PlatformAppDeployment[]; now: number }) {
  return (
    <ScrollTable caption="Deployments" className="admin-table admin-table--app-deployments">
      <thead>
        <tr>
          <th scope="col">Started</th>
          <th scope="col">Status</th>
          <th scope="col">Cause</th>
          <th scope="col">Commit</th>
          <th scope="col">
            <span className="admin-sr-only">In DigitalOcean</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {deployments.map((deployment) => (
          <tr key={deployment.id}>
            <th scope="row">
              <time dateTime={deployment.createdAt} title={formatDateTime(deployment.createdAt)}>
                {formatAge(deployment.createdAt, now)}
              </time>
            </th>
            <td>
              <RecordStatus tone={OUTCOME_TONES[deployment.outcome]}>
                {deploymentWords(deployment)}
              </RecordStatus>
            </td>
            <td>{deployment.cause ?? <span className="admin-muted">Not given</span>}</td>
            <td>
              {deployment.commit ? (
                <code>{deployment.commit.slice(0, 7)}</code>
              ) : (
                <span className="admin-muted">None</span>
              )}
            </td>
            <td>
              <a
                href={deployment.consoleUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Steps and logs of the deployment of ${formatDateTime(deployment.createdAt)} in DigitalOcean`}
              >
                Steps and logs
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

/** The deployment the page follows, in a live region: under way, then how it ended. */
function FollowedDeployment({
  action,
  deployment,
  following,
  gaveUp,
  failed,
  onCheck,
}: {
  action: PlatformAppOperation | null;
  deployment: PlatformAppDeployment;
  following: boolean;
  gaveUp: boolean;
  failed: boolean;
  onCheck: () => void;
}) {
  const what = action ? OPERATIONS[action].label : 'A deployment';
  let words: string;
  if (following) words = `${what} under way: ${deploymentWords(deployment)}.`;
  else if (gaveUp) words = `${what} is still running or could not be read; this page stopped following it.`;
  else if (deployment.outcome === 'success') words = `${what} finished: the deployment is live.`;
  else if (deployment.outcome === 'failed')
    words = `${what} failed in DigitalOcean. Its steps and logs say why.`;
  else words = `${what} ended: ${deploymentWords(deployment)}.`;
  return (
    <div
      className="admin-dev-note admin-app-follow"
      role="status"
      aria-live="polite"
      data-outcome={following ? 'in_progress' : deployment.outcome}
    >
      <p>
        {following ? <span className="admin-spinner" aria-hidden="true" /> : null}
        {words}{' '}
        {failed && following ? (
          <span className="admin-muted">(the last check failed; trying again)</span>
        ) : null}
      </p>
      <p className="admin-row-actions">
        <External href={deployment.consoleUrl}>Open the deployment in DigitalOcean</External>
        {gaveUp ? (
          <button type="button" className="admin-action" onClick={onCheck}>
            Check again
          </button>
        ) : null}
      </p>
    </div>
  );
}

interface DialogTarget {
  operation: PlatformAppOperation;
  /** A component restart names it; null acts on the whole app. */
  component: string | null;
}

/**
 * One App Platform app (/apps/:appId): its state, components and deployments, the controls (platform admins), and links
 * into DigitalOcean for everything else. After an operation the page follows the deployment it started (that
 * deployment only, every 10 seconds) and reads the app again when it ends.
 */
export function AppPage() {
  const { appId = '' } = useParams();
  const { api } = useAdmin();
  const abilities = useAbilities();
  const now = useNow(1000);
  const valid = UUID.test(appId);
  // The opening read is audited; every later one (Check again, after an operation) is a refresh.
  const [reads, setReads] = useState(0);
  const reread = () => setReads((n) => n + 1);
  const key = valid ? `app\u0000${appId}\u0000${reads}` : null;
  const read = useAdminRead<AdminPlatformApp>(
    key,
    (signal) => api.readPlatformApp(appId, reads > 0, signal),
    `app\u0000${appId.toLowerCase()}`,
  );
  const answer = read.failure || read.data?.app.id !== appId.toLowerCase() ? null : read.data;
  const app = answer?.app ?? null;
  const failure = valid ? read.failure : ({ kind: 'not-found' } as const);

  const [operated, setOperated] = useState<{
    action: PlatformAppOperation;
    deployment: PlatformAppDeployment;
  } | null>(null);
  // The deployment under way as DigitalOcean last reported it, else the one the operation started (which DigitalOcean
  // may not list as under way yet). Once followed to its end, the operation keeps the ended one, so it is not followed
  // again and a later deployment (a GitHub deploy) is.
  const start = app?.inProgress ?? operated?.deployment ?? null;
  const follow = useDeploymentFollow(
    start,
    (deploymentId, signal) => api.readPlatformAppDeployment(appId, deploymentId, signal),
    (ended) => {
      // Health and Apps remembered the app as it was before this deployment.
      forgetReads();
      setOperated((current) =>
        current && current.deployment.id === ended.id ? { ...current, deployment: ended } : current,
      );
      reread();
    },
  );
  const dialog = useDialogTarget<DialogTarget>();
  const [forceBuild, setForceBuild] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const deploying = app?.state === 'deploying' || follow.following;
  const blocked = (operation: PlatformAppOperation) =>
    answer && app
      ? controlBlocked(operation, {
          controls: answer.controls,
          canOperate: abilities.operateApps,
          state: app.state,
          deploying,
        })
      : 'The app is still loading.';
  const open = (operation: PlatformAppOperation, component: string | null = null) => {
    if (blocked(operation)) return;
    setForceBuild(false);
    dialog.show({ operation, component });
  };
  const rereadOnConflict = (writeFailure: WriteFailure) => {
    if (writeFailure.kind === 'conflict') reread();
  };

  const title = app?.displayName ?? 'App';
  // Every reason a control is off, as visible text under the buttons (not only a tooltip): one line per distinct
  // reason, each button described by its own line (aria-describedby), so keyboard, touch and screen reader users get it.
  const reasonsId = useId();
  const shown: PlatformAppOperation[] = ['restart', 'redeploy'];
  const reasons = [...new Set(shown.map((op) => blocked(op)).filter((r): r is string => r !== null))];
  const reasonId = (operation: PlatformAppOperation) => {
    const reason = blocked(operation);
    return reason === null ? undefined : `${reasonsId}-${reasons.indexOf(reason)}`;
  };

  let body: ReactNode;
  if (failure) {
    body = (
      <AdminProblem
        failure={failure}
        subject="this app"
        onRetry={read.retry}
        action={
          failure.kind === 'not-found' || failure.kind === 'invalid-request' ? (
            <ActionLink primary to="/apps">
              Back to Apps
            </ActionLink>
          ) : undefined
        }
      />
    );
  } else if (!answer || !app) {
    body = <LoadingBlock label="Reading the app from DigitalOcean" />;
  } else {
    const status = APP_STATUS[app.status];
    body = (
      <div className="admin-stack">
        {answer.source === 'fake' ? (
          <div className="admin-dev-note" role="note">
            <p>
              Synthetic data: this environment reads Trace&apos;s local stand-in for DigitalOcean. Its
              controls change only the stand-in.
            </p>
          </div>
        ) : null}
        {follow.deployment && (follow.following || operated) ? (
          <FollowedDeployment
            action={operated?.action ?? null}
            deployment={follow.deployment}
            following={follow.following}
            gaveUp={follow.gaveUp}
            failed={follow.failure !== null}
            onCheck={reread}
          />
        ) : null}
        {answer.failures.length > 0 ? (
          <div className="admin-dev-note" role="note">
            <p>Some of DigitalOcean&apos;s answers are missing:</p>
            <ul>
              {answer.failures.map((item, index) => (
                <li key={index}>{describeFailure(item)}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <dl className="admin-facts admin-facts--summary" aria-label="App">
          <Fact term="State">
            <RecordStatus tone={APP_STATE[app.state].tone}>{APP_STATE[app.state].label}</RecordStatus>
          </Fact>
          <Fact term="Health">
            <RecordStatus tone={status.tone}>{status.label}</RecordStatus>
            {app.reasons.length > 0 ? (
              <ul className="admin-app-reasons">
                {app.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            ) : null}
          </Fact>
          <Fact term="Address">
            {app.liveUrl ? (
              <External href={app.liveUrl}>{app.host ?? app.liveUrl}</External>
            ) : (
              <span className="admin-muted">None</span>
            )}
          </Fact>
          <Fact term="Region">
            {app.region ? <code>{app.region}</code> : <span className="admin-muted">Not given</span>}
          </Fact>
          <Fact term="Live version">
            {app.deployment ? (
              <>
                {app.deployment.commit ? <code>{app.deployment.commit.slice(0, 7)}</code> : null} deployed{' '}
                <time dateTime={app.deployment.createdAt} title={formatDateTime(app.deployment.createdAt)}>
                  {formatAge(app.deployment.createdAt, now)}
                </time>
              </>
            ) : (
              <span className="admin-muted">Nothing deployed</span>
            )}
          </Fact>
          <Fact term="Created">
            {app.createdAt ? (
              <DateTimeText iso={app.createdAt} />
            ) : (
              <span className="admin-muted">Not given</span>
            )}
          </Fact>
        </dl>
        <PageSection
          title="Components"
          description="As the app's spec defines them, with their state now. Sizes, counts and settings change in DigitalOcean."
        >
          {app.components.length > 0 ? (
            <ComponentsTable
              app={app}
              restartBlocked={blocked('restart')}
              restartReasonId={reasonId('restart')}
              onRestart={(name) => open('restart', name)}
            />
          ) : (
            <p className="admin-muted">DigitalOcean reported no component.</p>
          )}
        </PageSection>
        <PageSection title="Deployments" description="The latest ten, newest first.">
          {app.recentDeploys.length > 0 ? (
            <DeploymentsTable deployments={app.recentDeploys} now={now} />
          ) : (
            <p className="admin-muted">DigitalOcean lists no deployment.</p>
          )}
        </PageSection>
        <PageSection
          title="In DigitalOcean"
          description="Everything else about this app lives in DigitalOcean's control panel."
        >
          <ul className="admin-inline-list" aria-label="This app in DigitalOcean">
            <li>
              <External href={app.links.logs}>Runtime logs</External>
            </li>
            <li>
              <External href={app.links.activity}>All deployments</External>
            </li>
            <li>
              <External href={app.links.settings}>Settings, spec and scaling</External>
            </li>
            <li>
              <External href={app.links.console}>Overview and insights</External>
            </li>
          </ul>
        </PageSection>
      </div>
    );
  }

  const control = (operation: PlatformAppOperation) => {
    const reason = blocked(operation);
    return (
      <button
        type="button"
        className="admin-action"
        aria-disabled={reason ? true : undefined}
        aria-describedby={reasonId(operation)}
        onClick={() => open(operation)}
      >
        {OPERATIONS[operation].label}
      </button>
    );
  };

  const target = dialog.target;
  const targetLabel = target?.component ?? app?.name ?? '';
  const builtFromGit = app?.components.some((c) => c.source === 'git') ?? false;

  return (
    <AdminPage title={title}>
      <Link className="admin-back" to="/apps">
        <span aria-hidden="true">&larr;</span> Apps
      </Link>
      <PageHeading
        eyebrow={answer ? `App, ${ENVIRONMENT_NAMES[answer.environment]}` : null}
        title={title}
        description={
          app ? (
            <span className="admin-heading-meta">
              <code>{app.name}</code>
              <RecordStatus tone={APP_STATE[app.state].tone}>{APP_STATE[app.state].label}</RecordStatus>
            </span>
          ) : null
        }
        actions={
          app ? (
            <div className="admin-row-actions admin-app-actions">
              {control('restart')}
              {control('redeploy')}
              {/* Stopping is done in DigitalOcean (its Archive, in the app's settings): no spec write from here. */}
              <a className="admin-action" href={app.links.settings} target="_blank" rel="noopener noreferrer">
                Stop in DigitalOcean
              </a>
              <a className="admin-action" href={app.links.console} target="_blank" rel="noopener noreferrer">
                Open in DigitalOcean
              </a>
              <button
                type="button"
                className="admin-action"
                onClick={reread}
                aria-disabled={read.loading || undefined}
              >
                Check again
              </button>
            </div>
          ) : null
        }
      />
      {app && reasons.length > 0 ? (
        <ul className="admin-muted admin-app-controls-note" aria-label="Why some controls are off">
          {reasons.map((reason, index) => (
            <li key={reason} id={`${reasonsId}-${index}`}>
              {reasons.length > 1 || shown.some((op) => blocked(op) === null)
                ? `${shown
                    .filter((op) => blocked(op) === reason)
                    .map((op) => OPERATIONS[op].label)
                    .join(', ')}: ${reason}`
                : reason}
            </li>
          ))}
        </ul>
      ) : null}
      <SavedNotice message={notice} />
      {body}
      {app && answer && target ? (
        <WriteDialog
          key={`operation-${dialog.key}`}
          open={dialog.open}
          onClose={dialog.close}
          // Production is named in the title itself, so nobody acts on a production app thinking it is dev's.
          title={OPERATIONS[target.operation].title(
            answer.environment === 'production' ? `${targetLabel} in Production` : targetLabel,
          )}
          description={OPERATIONS[target.operation].what}
          submitLabel={OPERATIONS[target.operation].submit}
          onSubmit={async () => {
            const request: PlatformAppOperationRequest =
              target.operation === 'restart'
                ? target.component
                  ? { action: 'restart', components: [target.component] }
                  : { action: 'restart' }
                : { action: 'redeploy', forceBuild };
            const result = await api.operatePlatformApp(app.id, request);
            forgetReads();
            if (result.deployment) setOperated({ action: target.operation, deployment: result.deployment });
            return undefined;
          }}
          onSaved={() => {
            setNotice(`${OPERATIONS[target.operation].label} of ${targetLabel} sent to DigitalOcean.`);
            reread();
          }}
          onFailed={rereadOnConflict}
        >
          <dl className="admin-facts" aria-label="What this acts on">
            <Fact term="App">
              {app.displayName} (<code>{app.name}</code>), {ENVIRONMENT_NAMES[answer.environment]}
            </Fact>
            {target.component ? <Fact term="Component">{target.component}</Fact> : null}
          </dl>
          {target.operation === 'redeploy' && builtFromGit ? (
            <CheckboxGroup
              legend="Build"
              choices={[
                {
                  value: 'force',
                  label: 'Rebuild from source',
                  description: 'Builds the components that come from Git again, without the build cache.',
                },
              ]}
              values={forceBuild ? ['force'] : []}
              onChange={(values) => setForceBuild(values.includes('force'))}
            />
          ) : null}
        </WriteDialog>
      ) : null}
    </AdminPage>
  );
}
